import { useEffect, useState } from "react";
import { Plus, Wrench, Warning, Trash, ClockClockwise, BellRinging, Camera, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader } from "../components/Shell";
import ExportButton from "../components/ExportButton";
import { api, currency, todayKey } from "../lib/api";
import { Modal, Field } from "./Fuel";
import { useStation } from "../lib/station";

const MAX_PHOTOS = 5;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_MIMES = ["image/jpeg", "image/png"];

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export default function Maintenance() {
  const { activeId } = useStation();
  const [equipment, setEquipment] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showEq, setShowEq] = useState(false);
  const [showLog, setShowLog] = useState(null);
  const [editingReminderId, setEditingReminderId] = useState(null);
  const [editingValue, setEditingValue] = useState(7);

  const refresh = async () => {
    if (!activeId) return;
    const [e, l] = await Promise.all([api.listEquipment(activeId), api.listMaintenance(activeId)]);
    setEquipment(e);
    setLogs(l);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  if (!activeId) return null;

  const today = todayKey();

  const saveReminder = async (eq) => {
    const val = Math.max(0, parseInt(editingValue, 10) || 0);
    await api.updateEquipment(eq.id, { reminder_days_before: val });
    setEditingReminderId(null);
    toast.success("Reminder lead time updated");
    refresh();
  };

  return (
    <div>
      <PageHeader
        title="Maintenance"
        subtitle={`${equipment.length} equipment items`}
        action={
          <div className="flex items-center gap-2">
            <ExportButton kind="maintenance" testId="export-maintenance" />
            <button
              onClick={() => setShowEq(true)}
              data-testid="add-equipment-btn"
              className="h-11 px-4 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] font-bold text-sm uppercase tracking-wider flex items-center gap-2"
            >
              <Plus size={16} weight="bold" /> Equip
            </button>
          </div>
        }
      />

      <div className="grid gap-3" data-testid="equipment-list">
        {equipment.length === 0 && (
          <div className="surface rounded-lg p-6 text-center text-sm text-gray-500">
            No equipment yet. Add a pump, dispenser, or POS to get started.
          </div>
        )}
        {equipment.map((e) => {
          const overdue = e.next_service_date && e.next_service_date < today;
          const dueSoon =
            e.next_service_date &&
            !overdue &&
            new Date(e.next_service_date) - new Date(today) <= 7 * 86400000;
          const eqLogs = logs.filter((l) => l.equipment_id === e.id);
          const editing = editingReminderId === e.id;
          return (
            <div
              key={e.id}
              className={`surface rounded-lg p-4 ${overdue ? "border-l-4 border-l-[#EF4444]" : dueSoon ? "border-l-4 border-l-[#F59E0B]" : ""}`}
              data-testid={`equipment-row-${e.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 rounded-lg grid place-items-center ${overdue ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[#FF6B00]/15 text-[#FF6B00]"}`}>
                    <Wrench size={20} weight="bold" />
                  </div>
                  <div>
                    <div className="font-bold text-white">{e.name}</div>
                    <div className="text-xs text-gray-500 capitalize">
                      {e.category} {e.location && `· ${e.location}`}
                    </div>
                    {e.next_service_date && (
                      <div className={`text-[11px] mt-1 inline-flex items-center gap-1 font-mono ${overdue ? "text-[#EF4444]" : dueSoon ? "text-[#F59E0B]" : "text-gray-400"}`}>
                        {overdue && <Warning size={12} weight="fill" />}
                        Next: {e.next_service_date}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setShowLog(e.id)}
                  data-testid={`log-maintenance-${e.id}`}
                  className="h-9 px-3 rounded-lg surface-elevated text-xs font-bold uppercase tracking-wider text-[#FF6B00] hover:bg-white/[0.04]"
                >
                  + Log
                </button>
              </div>

              <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between">
                <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500">
                  <BellRinging size={12} weight="bold" />
                  Remind
                  {editing ? (
                    <>
                      <input
                        type="number"
                        min="0"
                        value={editingValue}
                        onChange={(ev) => setEditingValue(ev.target.value)}
                        data-testid={`eq-reminder-input-${e.id}`}
                        className="ml-1 w-14 bg-[#0F1115] border border-white/10 rounded px-2 py-0.5 text-xs text-white"
                      />
                      <span className="ml-1">day(s) before</span>
                    </>
                  ) : (
                    <span className="ml-1 font-mono text-gray-300">
                      {e.reminder_days_before ?? 7} day(s) before
                    </span>
                  )}
                </div>
                {editing ? (
                  <div className="flex gap-1">
                    <button
                      onClick={() => setEditingReminderId(null)}
                      className="h-7 px-2 text-[10px] uppercase tracking-wider text-gray-400 hover:text-white"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => saveReminder(e)}
                      data-testid={`eq-reminder-save-${e.id}`}
                      className="h-7 px-2 rounded text-[10px] uppercase tracking-wider bg-[#FF6B00] text-white font-bold"
                    >
                      Save
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      setEditingReminderId(e.id);
                      setEditingValue(e.reminder_days_before ?? 7);
                    }}
                    data-testid={`eq-reminder-edit-${e.id}`}
                    className="text-[10px] uppercase tracking-wider text-[#FF6B00] font-bold"
                  >
                    Edit
                  </button>
                )}
              </div>

              {eqLogs.length > 0 && (
                <div className="mt-3 border-t border-white/[0.06] pt-3 grid gap-2">
                  {eqLogs.slice(0, 3).map((l) => (
                    <div key={l.id} className="text-xs">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2">
                          <ClockClockwise size={14} className="text-gray-500 mt-0.5" />
                          <div>
                            <div className="text-gray-200 font-medium">{l.description}</div>
                            <div className="text-gray-500 font-mono">
                              {l.date} {l.technician && `· ${l.technician}`}
                            </div>
                          </div>
                        </div>
                        {l.cost > 0 && <div className="font-mono text-gray-300">{currency(l.cost)}</div>}
                      </div>
                      {l.photos && l.photos.length > 0 && (
                        <div className="mt-2 flex gap-1 flex-wrap" data-testid={`log-photos-${l.id}`}>
                          {l.photos.map((src, i) => (
                            <a
                              key={i}
                              href={src}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block h-14 w-14 rounded-md overflow-hidden border border-white/[0.06] hover:border-[#FF6B00]/60"
                            >
                              <img src={src} alt={`log ${i + 1}`} className="w-full h-full object-cover" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={async () => {
                  await api.deleteEquipment(e.id);
                  toast.success("Equipment removed");
                  refresh();
                }}
                data-testid={`delete-equipment-${e.id}`}
                className="mt-3 text-xs text-gray-500 hover:text-[#EF4444] inline-flex items-center gap-1"
              >
                <Trash size={12} /> Delete
              </button>
            </div>
          );
        })}
      </div>

      {showEq && (
        <EquipmentForm
          stationId={activeId}
          onClose={() => setShowEq(false)}
          onCreated={() => {
            setShowEq(false);
            refresh();
          }}
        />
      )}
      {showLog && (
        <MaintenanceForm
          stationId={activeId}
          equipmentId={showLog}
          onClose={() => setShowLog(null)}
          onCreated={() => {
            setShowLog(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function EquipmentForm({ stationId, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("pump");
  const [location, setLocation] = useState("");
  const [nextDate, setNextDate] = useState("");
  const [reminderDays, setReminderDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name) return toast.error("Name required");
    setSubmitting(true);
    try {
      await api.createEquipment({
        station_id: stationId,
        name,
        category,
        location,
        next_service_date: nextDate || null,
        reminder_days_before: parseInt(reminderDays, 10) || 7,
      });
      toast.success("Equipment added");
      onCreated();
    } catch {
      toast.error("Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Add Equipment" testId="equipment-modal">
      <form onSubmit={submit} className="grid gap-3" data-testid="equipment-form">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} className="input-dark" data-testid="equipment-name" required />
        </Field>
        <Field label="Category">
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="input-dark" data-testid="equipment-category">
            <option value="pump">Pump</option>
            <option value="dispenser">Dispenser</option>
            <option value="tank">Tank</option>
            <option value="pos">POS</option>
            <option value="other">Other</option>
          </select>
        </Field>
        <Field label="Location">
          <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Island 2" className="input-dark" data-testid="equipment-location" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Next Service Date">
            <input type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className="input-dark" data-testid="equipment-nextdate" />
          </Field>
          <Field label="Remind (days before)">
            <input
              type="number"
              min="0"
              value={reminderDays}
              onChange={(e) => setReminderDays(e.target.value)}
              className="input-dark"
              data-testid="equipment-reminder"
            />
          </Field>
        </div>
        <button type="submit" disabled={submitting} data-testid="equipment-submit" className="h-14 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 font-bold uppercase tracking-wider text-white">
          {submitting ? "Saving..." : "Add Equipment"}
        </button>
      </form>
    </Modal>
  );
}

function MaintenanceForm({ stationId, equipmentId, onClose, onCreated }) {
  const [form, setForm] = useState({
    date: todayKey(),
    description: "",
    vendor: "",
    technician: "",
    cost: "",
    next_service_date: "",
  });
  const [photos, setPhotos] = useState([]); // array of data URLs
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos per log`);
      return;
    }
    const next = [];
    for (const f of files) {
      if (!ALLOWED_MIMES.includes(f.type)) {
        toast.error(`${f.name}: only JPEG/PNG allowed`);
        continue;
      }
      if (f.size > MAX_PHOTO_BYTES) {
        toast.error(`${f.name}: exceeds 5MB`);
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      const url = await readAsDataURL(f);
      next.push(url);
    }
    setPhotos((p) => [...p, ...next].slice(0, MAX_PHOTOS));
  };

  const removePhoto = (idx) => setPhotos((p) => p.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.description) return toast.error("Description required");
    setSubmitting(true);
    try {
      await api.createMaintenance({
        station_id: stationId,
        equipment_id: equipmentId,
        ...form,
        cost: parseFloat(form.cost) || 0,
        next_service_date: form.next_service_date || null,
        photos,
      });
      toast.success("Maintenance logged");
      onCreated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to log maintenance");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Log Maintenance" testId="maintenance-modal">
      <form onSubmit={submit} className="grid gap-3" data-testid="maintenance-form">
        <Field label="Date">
          <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="input-dark" data-testid="maint-date" />
        </Field>
        <Field label="Description">
          <input value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="What was done?" className="input-dark" data-testid="maint-desc" required />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Vendor">
            <input value={form.vendor} onChange={(e) => set("vendor", e.target.value)} className="input-dark" data-testid="maint-vendor" />
          </Field>
          <Field label="Technician">
            <input value={form.technician} onChange={(e) => set("technician", e.target.value)} className="input-dark" data-testid="maint-tech" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Cost ($)">
            <input type="number" step="0.01" value={form.cost} onChange={(e) => set("cost", e.target.value)} className="input-dark" data-testid="maint-cost" />
          </Field>
          <Field label="Next Service">
            <input type="date" value={form.next_service_date} onChange={(e) => set("next_service_date", e.target.value)} className="input-dark" data-testid="maint-next" />
          </Field>
        </div>

        <Field label={`Photos (${photos.length}/${MAX_PHOTOS} · JPEG or PNG · 5MB each)`}>
          <div className="grid grid-cols-3 gap-2">
            {photos.map((src, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden surface-elevated" data-testid={`maint-photo-${i}`}>
                <img src={src} alt={`upload ${i + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  data-testid={`maint-photo-remove-${i}`}
                  className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 grid place-items-center text-white hover:bg-[#EF4444]"
                  aria-label="Remove photo"
                >
                  <X size={12} weight="bold" />
                </button>
              </div>
            ))}
            {photos.length < MAX_PHOTOS && (
              <label
                className="aspect-square rounded-lg border-2 border-dashed border-white/[0.12] hover:border-[#FF6B00]/60 grid place-items-center cursor-pointer text-gray-500 hover:text-[#FF6B00] transition-colors"
                data-testid="maint-photo-upload-label"
              >
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    onFiles(e.target.files);
                    e.target.value = "";
                  }}
                  data-testid="maint-photo-input"
                />
                <div className="flex flex-col items-center gap-1">
                  <Camera size={22} weight="bold" />
                  <span className="text-[10px] uppercase tracking-wider font-bold">Add</span>
                </div>
              </label>
            )}
          </div>
        </Field>

        <button type="submit" disabled={submitting} data-testid="maint-submit" className="h-14 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-60 font-bold uppercase tracking-wider text-white">
          {submitting ? "Saving..." : "Save Log"}
        </button>
      </form>
    </Modal>
  );
}
